import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutGrid, MousePointerClick, Share2, LineChart, ShieldCheck, Moon,
  ArrowRight, TrendingUp, Target, Wallet, GitBranch, Users, NotebookPen,
} from "lucide-react";

function PreviewSpark({ up = true }) {
  const d = up
    ? "M0,30 L12,26 L24,28 L36,20 L48,22 L60,14 L72,16 L84,8 L96,10 L108,4"
    : "M0,8 L12,12 L24,10 L36,18 L48,14 L60,22 L72,20 L84,26 L96,24 L108,30";
  return (
    <svg viewBox="0 0 108 34" className={`h-8 w-full ${up ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"}`}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function HeroPreview() {
  return (
    <div className="relative rounded-2xl border bg-card p-3 shadow-[0_30px_80px_rgba(2,6,23,0.18)]">
      <div className="mb-2 flex items-center gap-1.5 px-1">
        <span className="h-2 w-2 rounded-full bg-[hsl(var(--loss))]/60" />
        <span className="h-2 w-2 rounded-full bg-[hsl(var(--warning))]/60" />
        <span className="h-2 w-2 rounded-full bg-[hsl(var(--profit))]/60" />
        <span className="ml-2 text-[10px] text-muted-foreground">my-workspace · edit mode</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border bg-surface-2 p-3">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><Wallet className="h-3 w-3" /> Balance</div>
          <div className="mt-1 font-display text-lg font-semibold tabular-nums">$12,534</div>
          <div className="text-[10px] font-medium text-[hsl(var(--profit))]">+25.3%</div>
        </div>
        <div className="rounded-xl border bg-surface-2 p-3">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><Target className="h-3 w-3" /> Win rate</div>
          <div className="mt-1 font-display text-lg font-semibold tabular-nums">66.3%</div>
          <div className="mt-1.5 h-1 w-full rounded-full bg-muted"><div className="h-full w-2/3 rounded-full bg-[hsl(var(--profit))]" /></div>
        </div>
        <div className="rounded-xl border border-dashed border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10 p-3">
          <div className="flex h-full items-center justify-center gap-1 text-[10px] font-medium text-[hsl(var(--accent))]">
            <MousePointerClick className="h-3.5 w-3.5" /> drop widget here
          </div>
        </div>
        <div className="col-span-2 rounded-xl border bg-surface-2 p-3">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><LineChart className="h-3 w-3" /> Equity curve</div>
          <PreviewSpark up />
          <PreviewSpark up />
        </div>
        <div className="rounded-xl border bg-surface-2 p-3">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><TrendingUp className="h-3 w-3" /> Monthly</div>
          <div className="mt-2 flex h-14 items-end gap-1">
            {[40, 65, 30, 80, 55, 70, 45, 90].map((h, i) => (
              <div key={i} className={`flex-1 rounded-sm ${i === 2 ? "bg-[hsl(var(--loss))]/70" : "bg-[hsl(var(--profit))]/70"}`} style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: LayoutGrid, title: "Build your own dashboard", text: "Drag, drop, resize. No two traders see the same page, because no two traders trade the same." },
  { icon: LineChart, title: "Analytics with teeth", text: "Equity curves, drawdown, profit factor, expectancy, symbol breakdowns, trading-hour heatmaps. Computed from every single trade." },
  { icon: MousePointerClick, title: "Drill into everything", text: "Every widget opens into a full breakdown. One click from a headline number to the trades behind it." },
  { icon: Share2, title: "Share read-only views", text: "Send investors a live link to a workspace you designed for them. No login, no edits, no stale screenshots." },
  { icon: ShieldCheck, title: "MT4 & MT5, read-only", text: "Connect with your investor password. We see your history. We never touch your trades." },
  { icon: Moon, title: "Light & dark, your call", text: "A precision interface in both themes. Flip whenever. It remembers." },
];

const PERSONAS = [
  { icon: NotebookPen, name: "Solo Trader", tag: "Am I improving?", text: "Win rate, balance, monthly P/L and a daily journal. No client lists, no version history, no noise." },
  { icon: GitBranch, name: "Algo Vendor", tag: "Prove it works.", text: "Version history, risk profile and verified performance. A workspace built to close the sale." },
  { icon: Users, name: "Fund Manager", tag: "Every client, at a glance.", text: "All client accounts with performance beside each name, plus the numbers investors actually ask about." },
];

export default function LandingPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      {/* nav */}
      <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Logo />
            <nav className="hidden items-center gap-4 text-sm text-muted-foreground md:flex">
              <Link to="/about" className="transition-colors hover:text-foreground" data-testid="nav-about-link">About</Link>
              <Link to="/contact" className="transition-colors hover:text-foreground" data-testid="nav-contact-link">Contact</Link>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button asChild data-testid="nav-open-app-button">
                <Link to="/app">Open workspace <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" data-testid="nav-signin-button">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild data-testid="nav-getstarted-button">
                  <Link to="/login">Get started free</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(1200px 600px at 20% 0%, rgba(56,189,248,0.14), transparent 60%), radial-gradient(900px 500px at 85% 10%, rgba(20,184,166,0.10), transparent 55%)" }} />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:py-20 lg:grid-cols-2 lg:py-24">
          <div className="fade-up">
            <Badge variant="secondary" className="mb-4 gap-1.5">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[hsl(var(--profit))]" />
              Trading analytics, rebuilt around you
            </Badge>
            <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              The trading dashboard <span className="text-primary">you</span> design.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              Win rate. Drawdown. Equity curves. Profit factor. Every number that moves your trading,
              as widgets you drag onto a workspace that's completely yours. Start from a template.
              Make it yours. Share it with anyone.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" data-testid="hero-cta-button">
                <Link to={user ? "/app" : "/login"}>Build your workspace <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <span className="text-xs text-muted-foreground">Free to start · demo accounts included · no card</span>
            </div>
            <div className="mt-10 grid max-w-md grid-cols-3 gap-4">
              {[["24", "widgets"], ["3", "starter templates"], ["MT4/5", "account sync"]].map(([v, l]) => (
                <div key={l}>
                  <div className="font-display text-2xl font-semibold tabular-nums">{v}</div>
                  <div className="text-xs text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "120ms" }}>
            <HeroPreview />
          </div>
        </div>
      </section>

      {/* features bento */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:py-20">
        <h2 className="font-display max-w-xl text-2xl font-semibold tracking-tight sm:text-3xl">
          One widget library. Infinite dashboards.
        </h2>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Strip away the jargon and it's simple: boxes that show numbers, charts and lists. You decide which boxes matter.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-2xl border bg-card p-5 transition-shadow duration-150 hover:shadow-[0_10px_30px_rgba(2,6,23,0.10)]">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                <f.icon className="h-4 w-4 text-primary" />
              </span>
              <h3 className="mt-3 font-display text-base font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* personas */}
      <section className="border-y bg-surface-2">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:py-20">
          <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Three traders. Three completely different pages.</h2>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">Start from a template built for how you trade. Then move, resize or delete anything.</p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {PERSONAS.map((p) => (
              <div key={p.name} className="rounded-2xl border bg-card p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/12">
                  <p.icon className="h-5 w-5 text-[hsl(var(--accent))]" />
                </span>
                <h3 className="font-display mt-4 text-lg font-semibold">{p.name}</h3>
                <p className="text-sm font-medium text-primary">{p.tag}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t">
        <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:py-24">
          <h2 className="font-display mx-auto max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">
            Stop squinting at someone else's dashboard.
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            Sign up, load a template, and explore 18 months of demo analytics before you connect a single account.
          </p>
          <Button asChild size="lg" className="mt-7" data-testid="footer-cta-button">
            <Link to={user ? "/app" : "/login"}>Get started free <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-6">
          <Logo />
          <nav className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link to="/about" className="hover:text-foreground">About</Link>
            <Link to="/contact" className="hover:text-foreground">Contact</Link>
          </nav>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} My Trading Room · Trading involves risk. Past performance is not indicative of future results.</p>
        </div>
      </footer>
    </div>
  );
}
